/**
 * BorrowerHeader — single source of truth for the top bar shown across the
 * borrower-facing pages (WelcomePage marketing, /apply flow, BorrowerLoginPage,
 * AccountCreationPage). Centralizes the LoanKnow logo, sign-in CTA, and the
 * authenticated "My Dashboard" link.
 *
 * Use the `transparent` variant on dark hero sections; use the default variant
 * on every other page.
 */
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { LogIn, LayoutDashboard } from 'lucide-react';
import { LK_FEATURES } from '../../config/features';

const BorrowerHeader = ({
  variant = 'default',          // 'default' | 'transparent'
  showSignIn = true,
  rightSlot = null,
  onLogoClick = null,
  signInPath = '/borrower/login', // marketing pages can point at /readiness?signin=1
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onLenderPage = pathname.startsWith('/lenders') || pathname.startsWith('/lender/');
  const isAuthenticated = useSelector((s) => s?.loanApplication?.isAuthenticated);
  // Pre-launch: no one can have an account yet, so the Sign In / Dashboard
  // links are misleading. Hide them entirely until the application backend
  // goes live (LK_FEATURES.applicationLive).
  const showAuthLinks = showSignIn && LK_FEATURES.applicationLive;

  const isTransparent = variant === 'transparent';
  const logoSrc = isTransparent ? '/assets/loanknow-logo-white.svg' : '/assets/loanknow-logo.svg';
  const fg = isTransparent ? 'white' : 'var(--lk-text-primary)';
  const linkColor = isTransparent ? 'rgba(255,255,255,0.95)' : 'var(--lk-primary-600)';

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (onLogoClick) onLogoClick();
    else navigate('/');
  };

  return (
    <header style={{
      borderBottom: isTransparent ? 'none' : '1px solid var(--lk-border)',
      backgroundColor: isTransparent ? 'transparent' : 'var(--lk-bg-surface-1)',
      padding: 'var(--lk-space-3) 0',
      width: '100%',
      position: isTransparent ? 'absolute' : 'relative',
      top: 0, left: 0, zIndex: 10,
      color: fg,
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: '0 var(--lk-space-4)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16,
      }}>
        <a href="/" onClick={handleLogoClick} style={{ display: 'inline-block' }}>
          <img src={logoSrc} alt="LoanKnow" style={{ height: 44, display: 'block' }} />
        </a>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* Lender outreach link — visible on public/borrower pages so lenders
              can self-serve into the marketplace landing page. Hidden when
              they're already on it. */}
          {!onLenderPage && (
            <a
              href="/lenders"
              onClick={(e) => { e.preventDefault(); navigate('/lenders'); }}
              style={{
                fontSize: 'var(--lk-text-sm)', fontWeight: 600,
                color: isTransparent ? 'rgba(255,255,255,0.85)' : 'var(--lk-text-secondary)',
                textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              For Lenders
            </a>
          )}

          {rightSlot}
          {showAuthLinks && (
            isAuthenticated ? (
              <a
                href="/borrower/dashboard"
                onClick={(e) => { e.preventDefault(); navigate('/borrower/dashboard'); }}
                style={{
                  fontSize: 'var(--lk-text-sm)', fontWeight: 600,
                  color: linkColor, textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <LayoutDashboard size={16} /> My Dashboard
              </a>
            ) : (
              <a
                href={signInPath}
                onClick={(e) => { e.preventDefault(); navigate(signInPath); }}
                style={{
                  fontSize: 'var(--lk-text-sm)', fontWeight: 600,
                  color: linkColor, textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <LogIn size={16} /> Sign In
              </a>
            )
          )}
        </div>
      </div>
    </header>
  );
};

export default BorrowerHeader;
